"use client";
import { useState, FormEvent } from "react";
import Link from "next/link";
import Image from "next/image";
import { getBrowserSupabaseClient } from "@wtc/supabase";
import { clearSupabaseCookies } from "@/lib/utils/auth/clear-cookies";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Mail } from "lucide-react";

export default function MagicLinkForm() {
  const supabase = getBrowserSupabaseClient();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Reset all states
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      // Clear existing cookies before sending magic link
      const clearResult = clearSupabaseCookies();
      console.log("Cleared cookies before magic link:", clearResult);

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${new URL(location.href).origin}/`,
        },
      });

      if (error) {
        setError(error.message);
        return;
      }

      // Reset form
      setEmail("");
      setSuccess("Please check your email for a magic link to log into the website.");
    } catch (err) {
      console.error("Magic link error:", err);
      setError("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="space-y-6">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <Image
              src="/logo_long.svg"
              alt="West Tennessee Consulting Hub"
              width={200}
              height={48}
              className="h-12 w-auto"
            />
          </div>
          <h2 className="text-2xl font-bold">Sign in</h2>
          <p className="text-muted-foreground">Hi, Welcome back</p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Mail className="mr-2 h-4 w-4" />
            Sign in
          </Button>
        </form>

        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            Forgot your password?{" "}
            <Link href="/auth/reset-password" className="text-primary hover:underline">
              Reset it here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
